import React, { useEffect, useState } from 'react';
import { authAPI } from './api/client';
import useUserStore from './store/userStore';
import { C } from './theme';
import { calcRoot, ROOT_DATA } from './utils/data';
import { calcPersonalYear } from './utils/numbers';

import OnboardingScreen from './screens/OnboardingScreen';
import HomeScreen from './screens/HomeScreen';
import ProfileScreen from './screens/ProfileScreen';
import LogScreen from './screens/LogScreen';
import CompareScreen from './screens/CompareScreen';
import CalendarScreen from './screens/CalendarScreen';
import TablesScreen from './screens/TablesScreen';
import CompoundScreen from './screens/CompoundScreen';
import JmmlScreen from './screens/JmmlScreen';
import HouseScreen from './screens/HouseScreen';
import DatesScreen from './screens/DatesScreen';
import MapScreen from './screens/MapScreen';

const TABS = [
  { id:'today',    label:'🔭 اليوم' },
  { id:'profile',  label:'◈ ملفي' },
  { id:'compare',  label:'⚖ مقارنة' },
  { id:'calendar', label:'📅 تقويم' },
  { id:'tables',   label:'📋 جداول' },
  { id:'compound', label:'⬡ مركبة' },
  { id:'jmml',     label:'🔤 الجُمَّل' },
  { id:'house',    label:'🏠 المنزل' },
  { id:'dates',    label:'📆 تواريخ' },
  { id:'map',      label:'🗺 الخريطة' },
  { id:'log',      label:'📜 السجل' },
];

function UserBar({ user, subscription }) {
  if (!user) return null;
  const now = new Date();
  const dayRoot = calcRoot(now.getDate(), now.getMonth()+1, now.getFullYear());
  const pyear = user.birth_day && user.birth_month
    ? calcPersonalYear(user.birth_day, user.birth_month)
    : user.personal_year ?? '?';
  const todayUsed = subscription?.today_used || 0;
  const asksLeft = subscription ? subscription.asks_per_day - todayUsed : '—';
  const rd = ROOT_DATA[user.root_number];

  const items = [
    { lbl:'مرحباً', val:user.name?.split(' ')[0]||'?', sm:true },
    { lbl:'جذرك', val:user.root_number??'?' },
    { lbl:'الميلاد', val:user.birth_day?`${user.birth_day} / ${user.birth_month} / ${user.birth_year}`:'—', sm:true },
    { lbl:'الكوكب', val:rd?.planet||'—', sm:true },
    { lbl:'المضاد', val:rd?.contra??'—' },
    { lbl:'رقم اليوم', val:dayRoot },
  ];

  return (
    <div style={{ background:C.bg2, border:`1px solid ${C.border}`, borderRadius:16, padding:'14px 18px', marginBottom:20, display:'flex', flexWrap:'wrap', alignItems:'center', justifyContent:'center' }}>
      {items.map((item, i) => (
        <React.Fragment key={item.lbl}>
          <div style={{ textAlign:'center', padding:'0 12px' }}>
            <span style={{ fontSize:9, color:C.text2, display:'block', marginBottom:3, letterSpacing:.5 }}>{item.lbl}</span>
            <span style={{ fontSize:item.sm?12:18, color:C.gold, fontWeight:700, fontFamily:item.sm?'Cairo,sans-serif':'Amiri,serif' }}>{item.val}</span>
          </div>
          {i < items.length-1 && <div style={{ width:1, height:32, background:C.border }} />}
        </React.Fragment>
      ))}
    </div>
  );
}

function MainApp({ onLogout }) {
  const [tab, setTab] = useState('today');
  const { user, subscription } = useUserStore();

  const renderTab = () => {
    const map = {
      today:    <HomeScreen />,
      profile:  <ProfileScreen onLogout={onLogout} />,
      compare:  <CompareScreen />,
      calendar: <CalendarScreen />,
      tables:   <TablesScreen />,
      compound: <CompoundScreen />,
      jmml:     <JmmlScreen />,
      house:    <HouseScreen />,
      dates:    <DatesScreen />,
      map:      <MapScreen />,
      log:      <LogScreen />,
    };
    return map[tab] || null;
  };

  return (
    <div style={{ maxWidth:780, margin:'0 auto', padding:'20px 16px 40px' }}>
      {/* Header */}
      <div style={{ textAlign:'center', padding:'36px 0 28px', borderBottom:`1px solid ${C.border}`, marginBottom:24 }}>
        <span style={{ fontSize:36, display:'block', marginBottom:10, animation:'glow 3s ease-in-out infinite' }}>✦</span>
        <h1 style={{ fontFamily:'Amiri, serif', fontSize:26, color:C.gold, letterSpacing:2, margin:'0 0 5px' }}>جذر الفلك — مرصد الأرقام</h1>
        <p style={{ fontSize:11, color:C.text2, margin:0 }}>د. ماري أفرام بر أنوشا · الأجزاء 1–3 · 693 صفحة</p>
      </div>

      {/* Book chips */}
      <div style={{ display:'flex', gap:6, justifyContent:'center', flexWrap:'wrap', marginBottom:20 }}>
        {['الجزء الأول ✓','الجزء الثاني ✓','الجزء الثالث ✓'].map(b=>(
          <span key={b} style={{ background:C.bg3, border:`1px solid ${C.border}`, borderRadius:20, padding:'4px 12px', fontSize:10, color:C.goldDim }}>{b}</span>
        ))}
      </div>

      <UserBar user={user} subscription={subscription} />

      {/* Tabs */}
      <div style={{ display:'flex', gap:4, flexWrap:'wrap', background:C.bg2, border:`1px solid ${C.border}`, borderRadius:14, padding:5, marginBottom:20 }}>
        {TABS.map(t => (
          <button key={t.id} type="button"
            style={{ flex:'1 1 auto', background:tab===t.id?C.bg4:'none', border:'none', borderRadius:10, padding:'10px 6px', color:tab===t.id?C.gold:C.text2, fontSize:12, cursor:'pointer', transition:'all .2s', textAlign:'center', fontFamily:'Cairo, sans-serif', whiteSpace:'nowrap', fontWeight:tab===t.id?600:'normal' }}
            onClick={() => setTab(t.id)}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div key={tab} style={{ animation:'fadeUp .3s ease' }}>
        {renderTab()}
      </div>

      <div style={{ textAlign:'center', padding:20, color:C.text2, fontSize:10, borderTop:`1px solid ${C.border}`, marginTop:20, letterSpacing:.5 }}>
        جذر الفلك · د. ماري أفرام بر أنوشا · الأجزاء 1–3 · 693 صفحة
      </div>
    </div>
  );
}

export default function App() {
  const [screen, setScreen] = useState('loading');
  const { setAll, logout } = useUserStore();

  useEffect(() => {
    // لا تسجيل دخول — نقرأ المستخدم من المتصفح مباشرة
    authAPI.me()
      .then(res => {
        setAll(res.data.user, 'local', res.data.subscription);
        setScreen(res.data.user?.onboarding_done ? 'main' : 'onboarding');
      })
      .catch(() => setScreen('onboarding'));
  }, []);

  function handleLogout() {
    localStorage.removeItem('jadhr_user');
    localStorage.removeItem('jadhr_logs');
    logout();
    setScreen('onboarding');
  }

  if (screen === 'loading') return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ fontFamily:'Amiri,serif', fontSize:48, color:C.gold, animation:'glow 2s infinite' }}>✦</div>
    </div>
  );

  if (screen === 'onboarding') return <OnboardingScreen onNavigate={setScreen} />;
  return <MainApp onLogout={handleLogout} />;
}
